import { Component, Element, h, Host, Prop } from "@stencil/core";

import { hasSlot, setPropsIfNull } from "../../../utils/utils";

/**
 * @slot - Slot for any content
 * @slot heading - Slot for `stzh-heading` element
 * @slot image - Slot for `stzh-figure` or `img` element
 * @slot lead - Slot for `stzh-text` element
 * @slot readspeaker - Slot for `stzh-readspeaker` element
 * @slot breadcrumb - Slot for `stzh-link` element
 * @slot menu - Slot for `stzh-link` element
 * @slot dateline - Slot for date or meta information
 */
@Component({
  tag: "stzh-pi-pagetitle",
  styleUrl: "stzh-pi-pagetitle.scss",
  scoped: true,
})
export class StzhPiPagetitle {
  /** Level */
  @Prop({ reflect: true }) level: 1 | 2 | 3 | 4 = 1;

  /** Automatic Hyphens */
  @Prop({ reflect: true }) hyphensEnabled: boolean = false;

  /** Author of the content */
  @Prop({ reflect: true }) author: string = "";

  /** Date of the content */
  @Prop({ reflect: true }) date: string = "";

  @Element() element: HTMLStzhPiPagetitleElement;

  private init = () => {
    const heading = this.element.querySelector('stzh-heading[slot="heading"], [slot="heading"] stzh-heading');
    const leads = this.element.querySelectorAll('stzh-text[slot="lead"], [slot="lead"] stzh-text');
    const readspeakerButton = this.element.querySelector('[slot="readspeaker"] stzh-button');

    if (heading) {
      setPropsIfNull(heading, {
        curve: "hero",
        level: "1",
      } as HTMLStzhHeadingElement);
    }

    if (readspeakerButton) {
      setPropsIfNull(readspeakerButton, {
        variant: "tertiary-plain",
        noPaddingLeft: true,
        noPaddingRight: true,
        size: "small",
        sizeMedium: "tiny",
        buttonAccesskey: "L",
      } as HTMLStzhButtonElement);
    }

    leads.forEach(lead => {
      setPropsIfNull(lead, {
        lead: true,
      } as HTMLStzhTextElement);
    });
  };

  connectedCallback() {
    this.init();
  }

  render() {
    const imageSlotUsed = hasSlot(this.element, "image");
    const leadSlotUsed = hasSlot(this.element, "lead");
    const readspeakerSlotUsed = hasSlot(this.element, "readspeaker");
    const menuSlotUsed = hasSlot(this.element, "menu");
    const hasArticleInfo = !!this.author || !!this.date;

    const classes = {
      "stzh-pi-pagetitle": true,
      "stzh-pi-pagetitle--has-image": imageSlotUsed,
      "stzh-pi-pagetitle--has-no-image": !imageSlotUsed,
      "stzh-pi-pagetitle--has-lead": leadSlotUsed,
      "stzh-pi-pagetitle--has-meta": readspeakerSlotUsed,
      "stzh-pi-pagetitle--has-menu": menuSlotUsed,
      "stzh-pi-pagetitle--has-article-info": hasArticleInfo,
      "stzh-pi-pagetitle--hyphens": this.hyphensEnabled,
      [`stzh-pi-pagetitle--level-${this.level}`]: !!this.level,
    };

    return (
      <Host has-menu={menuSlotUsed} has-article-info={hasArticleInfo}>
        <div class={classes}>
          <div class="stzh-pi-pagetitle__hero-container">
            <div class="stzh-pi-pagetitle__hero">
              <div class="stzh-pi-pagetitle__breadcrumbs">
                <slot name="breadcrumb"></slot>
              </div>

              <div class="stzh-pi-pagetitle__meta">
                <div class="stzh-pi-pagetitle__readspeaker">
                  <slot name="readspeaker"></slot>
                </div>
              </div>

              <div class="stzh-pi-pagetitle__dateline">
                <slot name="dateline"></slot>
              </div>

              <div class="stzh-pi-pagetitle__heading">
                <slot name="heading"></slot>
              </div>
            </div>
          </div>
          <div class="stzh-pi-pagetitle__image-lead-menu-container">
            <div class="stzh-pi-pagetitle__image-lead-menu">
              <div class="stzh-pi-pagetitle__menu">
                {menuSlotUsed && (
                  <div class="stzh-pi-pagetitle__menu-inner">
                    <slot name="menu"></slot>
                  </div>
                )}
              </div>

              {imageSlotUsed && (
                <div class="stzh-pi-pagetitle__image">
                  {[1, 2].indexOf(this.level) > -1 && (
                    <div class="stzh-pi-pagetitle__image-wrapper">
                      <stzh-ratio ratio={"16:9"} ratioSmall={"21:9"} class="stzh-pi-pagetitle__ratio">
                        <slot name={"image"}></slot>
                      </stzh-ratio>
                    </div>
                  )}
                </div>
              )}

              {menuSlotUsed && (
                <div class="stzh-pi-pagetitle__lead">
                  <slot name="lead"></slot>
                </div>
              )}
            </div>
          </div>

          {!menuSlotUsed && (
            <div class="stzh-pi-pagetitle__lead-container">
              <div class="stzh-pi-pagetitle__lead">
                <slot name="lead"></slot>
              </div>
            </div>
          )}

          {hasArticleInfo && (
            <div class="stzh-pi-pagetitle__article-info-wrapper">
              <div class="stzh-pi-pagetitle__article-info">
                {this.author && <p class="stzh-pi-pagetitle__author">{this.author}</p>}
                {this.date && <p class="stzh-pi-pagetitle__date">{this.date}</p>}
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
